import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { boolean, date, number, object, string } from 'yup';

import { useAlerta, useToast } from 'Component/Alerta';
import BotaoLink from 'Component/BotaoLink';
import Cabecalho from 'Component/Cabecalho';
import Form, {
  Botao,
  CaixaDeSelecao,
  CaixaDeTexto,
  Campo,
  Contexto,
  Selecao,
} from 'Component/Form';
import LayoutDefault from 'Component/Layout';
import Indisponibilidade from 'Interface/Indisponibilidades';
import { Erro } from 'Interface/Requisicao';
import { INDISPONIBILIDADES } from 'View/constants';
import { recuperarMensagemDeErro } from 'View/functions';
import {
  useEditarIndisponibilidadeMutation,
  useRecuperarIndisponibilidadeQuery,
} from 'View/Service/Default/Indisponibilidades';

const regras = object({
  dia_inteiro: boolean().nullable().optional(),
  tipo: number().required('Obrigatório'),
  agrupador: string().nullable().optional(),
  inicio: date().required('Obrigatório'),
  termino: date().required('Obrigatório'),
  motivo: string().nullable().optional(),
});

const EditarAdmin = (): React.ReactElement => {
  const navegacao = useNavigate();
  const toast = useToast();
  const alerta = useAlerta();
  const { usuarioId = 0, id = 0 } = useParams();
  const [editarIndisponibilidade] = useEditarIndisponibilidadeMutation();

  const { data: { indisponibilidade } = {} } =
    useRecuperarIndisponibilidadeQuery(id);

  const enviar = async (dados: Indisponibilidade): Promise<void> => {
    try {
      await editarIndisponibilidade({
        ...dados,
        dia_inteiro: [1, 3].includes(dados.tipo) ? 1 : (dados.dia_inteiro ? 1 : 0),
      }).unwrap();

      toast({ conteudo: 'Editado com sucesso!', tema: 'success' });

      navegacao(`/usuarios/editar/${usuarioId}`);
    } catch (error) {
      const teste = error as Erro;

      alerta({
        tema: 'danger',
        titulo: 'Erro!',
        conteudo: recuperarMensagemDeErro(teste.data.errors),
      });
    }
  };

  return (
    <LayoutDefault>
      <Cabecalho
        titulo="Editar um período de indisponibilidade"
        descricao="Preencha o formulário e clique em enviar para editar"
      />
      <Form onSubmit={enviar} regras={regras} inicial={indisponibilidade}>
        <div className="row justify-content-center">
          <div className="col-md-5">
            <div className="bg-white shadow-sm rounded p-4">
              <p>
                Caso queira solicitar somente um dia de folga, coloque a mesma
                data no campo início e término.
              </p>
              <div className="row">
                <div className="col-md-12">
                  <Selecao
                    name="tipo"
                    rotulo="Tipo"
                    opcoes={INDISPONIBILIDADES}
                  />
                </div>
                <Contexto>
                  {({ values }) => (
                    <React.Fragment>
                      {values.tipo && values.tipo == 2 && (
                        <div className="col-md-12">
                          <CaixaDeSelecao
                            name="dia_inteiro"
                            rotulo="Dia inteiro"
                          />
                        </div>
                      )}
                      {values.tipo == 1 && (
                        <div className="col-md-12">
                          <Campo
                            name="agrupador"
                            rotulo="Fator agrupador"
                            ajuda="Utilizado para agrupar férias que foram divididas. O agrupador colocado aqui pode ser qualquer coisa, desde que se repita somente nas férias correspondentes ao mesmo ciclo."
                          />
                        </div>
                      )}
                      <div className="col-md-6">
                        <Campo
                          type={
                            values['dia_inteiro'] ||
                            [1, 3].includes(values.tipo)
                              ? 'date'
                              : 'datetime-local'
                          }
                          name="inicio"
                          rotulo="Início"
                        />
                      </div>
                      <div className="col-md-6">
                        <Campo
                          type={
                            values['dia_inteiro'] ||
                            [1, 3].includes(values.tipo)
                              ? 'date'
                              : 'datetime-local'
                          }
                          name="termino"
                          rotulo="Término"
                        />
                      </div>
                    </React.Fragment>
                  )}
                </Contexto>
              </div>
              <CaixaDeTexto name="motivo" rotulo="Motivo" />
              <div className="mt-5">
                <Botao>Enviar</Botao>
                <BotaoLink
                  tema="light"
                  to="/indisponibilidades"
                  className="ms-2"
                >
                  Cancelar
                </BotaoLink>
              </div>
            </div>
          </div>
        </div>
      </Form>
    </LayoutDefault>
  );
};

export default EditarAdmin;
